"use client";

import { useSearchParams } from "next/navigation";
import LoginSignup from "@/containers/website/profile/loginSignup/LoginSignup";
import ProfileModal from "@/containers/website/profile/ProfileModal";
import staticData from "@/public/data/landing.json";
import { Button } from "@/shared/components/common/button";
import Flex from "@/shared/components/common/Flex";
import List from "@/shared/components/common/List";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "@/shared/components/common/navigation";
import Typography from "@/shared/components/common/Typography";
import useScroll from "@/shared/hooks/useScroll";
import type { WebsiteSettings } from "@/types/services/public/apis/Settings";
import { ChatTeardropDots, User } from "@phosphor-icons/react";
import Image from "next/image";
import Link from "next/link";
import DesktopHeaderDropdown from "./DesktopHeaderDropdown";
import ThemeSwitch from "../footer/components/ThemeSwitch";
import Separator from "@/shared/components/common/separator";
import {useEffect, useState} from "react";
import { Dialog, DialogContent } from "@/shared/components/common/Dialog";
import {getCookie, setCookie} from "@/libs/utils";

interface DesktopHeaderProps {
  profileData: ProfileResult | null;
  settings: WebsiteSettings;
  logoutAction: () => Promise<void>;
}

export default function DesktopHeader({
  profileData,
  settings,
  logoutAction,
}: DesktopHeaderProps) {
  const searchParams = useSearchParams();
  const utmSource = searchParams.get("utm_source");
  const { nav, btns } = staticData.header;
  const { direction } = useScroll();

  const { header_logos } = settings.theme;
    const [adsPopupOpen, setAdsPopupOpen] = useState<boolean>(false);
    const [b2bAdsPopupOpen, setB2bAdsPopupOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!process.env.NEXT_PUBLIC_BASE_URL?.includes("mdsafar")) return;
        if (utmSource === "safarmarket") return;

        const adsCookie = getCookie("ads_popup_shown");
        const b2bCookie = getCookie("b2b_ads_popup_shown");

        // حالت کاربر ساین نشده
        if (!profileData?.is_sign && !adsCookie) {
            setAdsPopupOpen(true);
            setCookie("ads_popup_shown", "true", 1); // 1 روز
        }

        // حالت کاربر ساین شده
        if (profileData?.is_sign && !b2bCookie) {
            setB2bAdsPopupOpen(true);
            setCookie("b2b_ads_popup_shown", "true", 1);
        }
    }, [profileData?.is_sign, utmSource]);
  return (
    <>
      <header
        className={`w-full h-16 flex md:px-8 border-b border-grayColor bg-background max-md:hidden sticky top-0 right-0 left-0 z-40 ${
          direction === "down" && "md:-translate-y-full"
        } transition duration-200 easeInOutCirc`}
      >
        <Flex className="xl:basis-4/5 basis-full items-center justify-between m-auto">
          {/* - * - * - Logo - * - * - */}
          <Flex className="basis-1/5 max-md:basis-2/4 max-lg:w-1/2 items-center justify-center gap-x-8">
            {header_logos && (
              <Link href={"/"}>
                <Image
                  src={header_logos}
                  priority
                  width={400}
                  height={200}
                  className="h-12 w-auto"
                  alt="logo"
                  quality={75}
                />
              </Link>
            )}
          </Flex>
          {/* - * - * - Primary Buttons - * - * - */}
          <Flex className="basis-3/5 max-lg:hidden items-center">
            <NavigationMenu>
              <NavigationMenuList className="relative flex items-center justify-evenly flex-row-reverse">
                <List
                  items={nav.links}
                  renderItems={({ title, link, state, navItems }) => (
                    <NavigationMenuItem key={`nav-${link}`}>
                      {state === "disabled" ? (
                        <Button
                          variant={"ghost"}
                          disabled={true}
                          className="text-sm text-ghost hover:text-primary font-medium border-none shadow-none! rounded-xs"
                        >
                          {title}
                        </Button>
                      ) : (
                        <>
                          {navItems && Object.keys(navItems) ? (
                            <DesktopHeaderDropdown
                              navItems={navItems}
                              title={title}
                              link={link}
                            />
                          ) : (
                            <Link href={link}>
                              <Button
                                variant={"ghost"}
                                className="text-sm text-ghost hover:text-primary font-medium border-none shadow-none! rounded-xs"
                              >
                                {title}
                              </Button>
                            </Link>
                          )}
                        </>
                      )}
                    </NavigationMenuItem>
                  )}
                />
              </NavigationMenuList>
            </NavigationMenu>
          </Flex>

          {/* - * - * - Secondary Buttons - * - * - */}
          <Flex className="basis-1/5 max-md:basis-1/4 max-lg:w-1/2 justify-center items-center md:gap-x-2">
            {/* support & myTrip */}

            <List
              items={btns}
              renderItems={({ text, name, link }) => {
                if (name === "loginSignup") return;
                return (
                  <Link href={link} key={name}>
                    <Button
                      variant={"outline"}
                      className="border-none shadow-none! rounded-xs max-sm:hidden"
                    >
                      <ChatTeardropDots
                        weight="duotone"
                        className="w-5 h-5 fill-primary md:mx-2"
                      />

                      {text}
                    </Button>
                  </Link>
                );
              }}
            />

            {profileData ? (
              <ProfileModal
                profileData={profileData}
                logoutAction={logoutAction}
              >
                <Button
                  variant={"outline"}
                  className="border-none shadow-none! rounded-xs"
                >
                  <Flex className="items-center gap-x-2">
                    <User weight="duotone" className="fill-primary w-5 h-5" />
                    <Typography className="font-medium max-sm:hidden">
                      {profileData.name ?? profileData.phone}
                    </Typography>
                  </Flex>
                </Button>
              </ProfileModal>
            ) : (
              <LoginSignup>
                <Button
                  variant={"outline"}
                  className="border-none shadow-none! rounded-xs"
                >
                  <User weight="duotone" className="fill-primary mx-2" />
                  {btns[1].text}
                </Button>
              </LoginSignup>
            )}

            <Separator direction="vertical" className="!h-5 bg-muted" />

            <ThemeSwitch />
          </Flex>
        </Flex>
      </header>
      <Dialog open={adsPopupOpen} onOpenChange={() => setAdsPopupOpen(false)}>
        <DialogContent className="max-sm:w-[95%] rounded-xs sm:max-w-[70rem] bg-background p-0">
          <Image
            src="/images/ads-popup1.webp"
            alt="Advertisement"
            className="w-full h-auto rounded-xs"
            width={1000}
            height={600}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={b2bAdsPopupOpen}
        onOpenChange={() => setB2bAdsPopupOpen(false)}
      >
        <DialogContent className="max-sm:w-[95%] rounded-xs sm:max-w-[70rem] bg-background p-0">
          <Image
            src="/images/b2b-popup.webp"
            alt="Advertisement"
            className="w-full h-auto rounded-xs"
            width={1000}
            height={600}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
